import React, { useEffect, useMemo, useRef, useState } from "react";
import "./InstallAppButton.css";

const DISMISS_KEY = "hideInstallPromptUntil";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const INSTALL_STATE_EVENT = "mfp:pwa-install-state";

let deferredInstallPrompt = null;
let appInstalled = false;

const now = () => Date.now();

const isStandaloneMode = () => {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator?.standalone === true ||
    appInstalled
  );
};

const readDismissed = () => {
  if (typeof window === "undefined") return false;

  try {
    return Number(window.localStorage.getItem(DISMISS_KEY) || 0) > now();
  } catch (error) {
    return false;
  }
};

const setDismissed = () => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(DISMISS_KEY, String(now() + DISMISS_MS));
  } catch (error) {}

  window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
};

const clearDismissed = () => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(DISMISS_KEY);
  } catch (error) {}
};

const getPlatformInfo = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isAndroid: false,
      isChromeIos: false,
      isEdgeIos: false,
      isFirefoxIos: false,
      isIos: false,
      isMobile: false,
      isSafari: false,
    };
  }

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const isIpadOs = platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isIos = /iPad|iPhone|iPod/.test(ua) || isIpadOs;
  const isAndroid = /Android/i.test(ua);
  const isChromeIos = /CriOS/i.test(ua);
  const isEdgeIos = /EdgiOS/i.test(ua);
  const isFirefoxIos = /FxiOS/i.test(ua);
  const isSafari =
    isIos &&
    /Safari/i.test(ua) &&
    !isChromeIos &&
    !isEdgeIos &&
    !isFirefoxIos;
  const isMobile =
    isIos ||
    isAndroid ||
    window.matchMedia?.("(max-width: 768px)")?.matches === true;

  return {
    isAndroid,
    isChromeIos,
    isEdgeIos,
    isFirefoxIos,
    isIos,
    isMobile,
    isSafari,
  };
};

const trackPwaEvent = () => {};

const getInstructions = (platform) => {
  if (platform.isIos && platform.isSafari) {
    return {
      title: "Install MyFarmingPal on your iPhone",
      steps: [
        "Tap the Share button in Safari.",
        "Scroll and tap Add to Home Screen.",
        "Tap Add.",
      ],
      note: "Then MyFarmingPal will open from your home screen like an app.",
    };
  }

  if (platform.isIos) {
    return {
      title: "Install MyFarmingPal on your iPhone",
      steps: ["Open MyFarmingPal in Safari, then tap Share > Add to Home Screen."],
      note: "Chrome and Edge on iPhone use Safari for home-screen installation.",
    };
  }

  return {
    title: "Install MyFarmingPal",
    steps: ["Tap your browser menu, then tap Install app or Add to Home Screen."],
    note: "When your browser supports direct install, MyFarmingPal will open like an app.",
  };
};

const InstallAppButton = ({
  buttonLabel = "Install MyFarmingPal",
  className = "",
  compact = false,
  description = "Add it to your phone so local stands, jobs, messages, and records are one tap away.",
  source = "install_app_button",
  title = "Use MyFarmingPal like an app",
  variant = "button",
}) => {
  const platform = useMemo(() => getPlatformInfo(), []);
  const [hasPrompt, setHasPrompt] = useState(Boolean(deferredInstallPrompt));
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
  const [isDismissed, setIsDismissed] = useState(readDismissed);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const seenTrackedRef = useRef(false);

  useEffect(() => {
    const refreshState = () => {
      setHasPrompt(Boolean(deferredInstallPrompt));
      setIsStandalone(isStandaloneMode());
      setIsDismissed(readDismissed());
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredInstallPrompt = event;
      clearDismissed();
      refreshState();
      window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
    };

    const handleAppInstalled = () => {
      appInstalled = true;
      deferredInstallPrompt = null;
      trackPwaEvent("pwa_install_accepted", { source });
      refreshState();
      window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener(INSTALL_STATE_EVENT, refreshState);

    const displayModeQuery = window.matchMedia?.("(display-mode: standalone)");
    displayModeQuery?.addEventListener?.("change", refreshState);

    refreshState();

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener(INSTALL_STATE_EVENT, refreshState);
      displayModeQuery?.removeEventListener?.("change", refreshState);
    };
  }, [source]);

  const canShowManualInstall = platform.isIos || platform.isAndroid || platform.isMobile;
  const shouldShow = !isStandalone && !isDismissed && (hasPrompt || canShowManualInstall);
  const installActionLabel =
    platform.isIos || platform.isAndroid
      ? "Add to your phone"
      : hasPrompt
        ? buttonLabel
        : "Add to this device";

  useEffect(() => {
    if (!shouldShow || seenTrackedRef.current) return;
    seenTrackedRef.current = true;
    trackPwaEvent("pwa_install_button_seen", {
      source,
      install_prompt_available: hasPrompt,
    });
  }, [hasPrompt, shouldShow, source]);

  if (!shouldShow) return null;

  const instructions = getInstructions(platform);

  const dismiss = (eventName = "pwa_install_dismissed") => {
    deferredInstallPrompt = null;
    setInstructionsOpen(false);
    setDismissed();
    trackPwaEvent(eventName, { source });
  };

  const openInstructions = () => {
    setInstructionsOpen(true);
    trackPwaEvent("pwa_install_instructions_opened", { source });
  };

  const handleInstallClick = async () => {
    if (!deferredInstallPrompt) {
      openInstructions();
      return;
    }

    const promptEvent = deferredInstallPrompt;
    deferredInstallPrompt = null;

    try {
      trackPwaEvent("pwa_install_prompt_opened", { source });
      promptEvent.prompt();
      const choice = await promptEvent.userChoice;

      if (choice?.outcome === "accepted") {
        appInstalled = true;
        clearDismissed();
        setIsStandalone(true);
        trackPwaEvent("pwa_install_accepted", { source });
      } else {
        dismiss("pwa_install_dismissed");
      }
    } catch (error) {
      openInstructions();
    } finally {
      window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
    }
  };

  const renderModal = () =>
    instructionsOpen ? (
      <div
        className="install-app-modal-backdrop"
        role="presentation"
        onClick={() => setInstructionsOpen(false)}
      >
        <section
          className="install-app-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="install-app-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="install-app-modal-header">
            <h2 id="install-app-modal-title">{instructions.title}</h2>
            <button
              type="button"
              className="install-app-close"
              aria-label="Close install instructions"
              onClick={() => setInstructionsOpen(false)}
            >
              x
            </button>
          </div>

          <ol className="install-app-steps">
            {instructions.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          <p>{instructions.note}</p>

          <div className="install-app-modal-actions">
            <button type="button" className="btn-primary" onClick={() => setInstructionsOpen(false)}>
              Got it
            </button>
            <button type="button" className="btn-soft" onClick={() => dismiss()}>
              Not now
            </button>
          </div>
        </section>
      </div>
    ) : null;

  if (variant === "card") {
    return (
      <>
        <section className={`install-app-card${compact ? " compact" : ""} ${className}`.trim()}>
          <div>
            <p className="install-app-eyebrow">One-tap access</p>
            <h2>{title}</h2>
            {!compact && <p>{description}</p>}
          </div>
          <div className="install-app-card-actions">
            <button type="button" className="btn-primary" onClick={handleInstallClick}>
              {installActionLabel}
            </button>
            <button type="button" className="btn-soft" onClick={() => dismiss()}>
              Not now
            </button>
          </div>
        </section>
        {renderModal()}
      </>
    );
  }

  if (variant === "menu") {
    return (
      <>
        <button
          type="button"
          className={`mobile-menu-action install-app-menu-action ${className}`.trim()}
          onClick={handleInstallClick}
        >
          {platform.isIos || platform.isAndroid ? "Add to your phone" : "Install MyFarmingPal"}
        </button>
        {renderModal()}
      </>
    );
  }

  if (variant === "publicNav") {
    return (
      <>
        <button
          type="button"
          className={`public-nav-link install-app-public-nav ${className}`.trim()}
          onClick={handleInstallClick}
        >
          Install MyFarmingPal
        </button>
        {renderModal()}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`install-app-button ${className}`.trim()}
        onClick={handleInstallClick}
      >
        {installActionLabel}
      </button>
      {renderModal()}
    </>
  );
};

export default InstallAppButton;
